import { url } from '@vuelidate/validators';
import { processVariable, buildWhatsAppProcessedParams } from '@chatwoot/utils';

// Constants and pure template helpers are shared with the mobile app via
// @chatwoot/utils so the logic lives in one place.
export {
  MEDIA_FORMATS,
  COMPONENT_TYPES,
  findComponentByType,
  processVariable,
  renderTemplatePreview,
} from '@chatwoot/utils';

export const DEFAULT_LANGUAGE = 'en';
export const DEFAULT_CATEGORY = 'UTILITY';

export const allKeysRequired = value => {
  const keys = Object.keys(value);
  return keys.every(key => value[key]);
};

export const replaceTemplateVariables = (templateText, processedParams) => {
  return templateText.replace(/{{([^}]+)}}/g, (match, variable) => {
    const variableKey = processVariable(variable);
    return processedParams.body?.[variableKey] || `{{${variable}}}`;
  });
};

// The media-header flag is derived from the template inside the shared helper;
// the second argument is kept for backwards-compatible call sites.
export const buildTemplateParameters = template =>
  buildWhatsAppProcessedParams(template);

// --- WhatsApp typed body parameters (currency) -----------------------------
// The shared @chatwoot/utils scaffold only emits plain-text body values. Meta's
// Cloud API, however, accepts a typed `currency` body parameter (an object, not
// a `{{n}}` text variable) — exactly what utility/Pix "amount" templates need.
// The backend already builds it (Whatsapp::PopulateTemplateParametersService#
// build_currency_parameter); we just need the composer to emit the object.
export const CURRENCY_CODES = ['BRL', 'USD', 'EUR', 'GBP', 'ARS', 'PYG'];
export const DEFAULT_CURRENCY_CODE = 'BRL';

const CURRENCY_LOCALE = {
  BRL: 'pt-BR',
  USD: 'en-US',
  EUR: 'de-DE',
  GBP: 'en-GB',
  ARS: 'es-AR',
  PYG: 'es-PY',
};

// Parse a user-typed amount into a Number. Accepts Brazilian ("1.234,56") and
// plain ("1234.56" / "100") formats. Returns NaN when empty/invalid.
export const parseAmountInput = raw => {
  if (raw === null || raw === undefined) return NaN;
  let s = String(raw).trim();
  if (!s) return NaN;
  s = s.replace(/[^\d.,-]/g, '');
  if (!/\d/.test(s)) return NaN;
  if (s.includes(',')) {
    // Brazilian format: '.' as thousands separator, ',' as decimal separator.
    s = s.replace(/\./g, '').replace(',', '.');
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

export const formatCurrencyFallback = (amount, code = DEFAULT_CURRENCY_CODE) => {
  try {
    return new Intl.NumberFormat(CURRENCY_LOCALE[code] || 'pt-BR', {
      style: 'currency',
      currency: code,
    }).format(amount);
  } catch (error) {
    return `${code} ${amount}`;
  }
};

// Build Meta's currency body parameter, or null when the amount is empty/invalid.
// Shape mirrors Whatsapp::PopulateTemplateParametersService#build_currency_parameter.
export const buildCurrencyParam = (rawAmount, code = DEFAULT_CURRENCY_CODE) => {
  const amount = parseAmountInput(rawAmount);
  if (!Number.isFinite(amount)) return null;
  return {
    type: 'currency',
    code,
    amount_1000: Math.round(amount * 1000),
    fallback_value: formatCurrencyFallback(amount, code),
  };
};

export const isCurrencyParam = value =>
  !!value && typeof value === 'object' && value.type === 'currency';

// For preview/validation helpers that expect string values: replace currency
// objects with their display (fallback) string.
export const flattenBodyForPreview = (body = {}) => {
  const out = {};
  Object.keys(body || {}).forEach(key => {
    const value = body[key];
    out[key] = isCurrencyParam(value) ? value.fallback_value || '' : value;
  });
  return out;
};

// 360dialog templates may not have a provider ID.
export const getTemplateKey = template =>
  template.id ?? `${template.name}:${template.language}`;

export const isValidTemplateMediaUrl = value =>
  !value || (/^https?:\/\//i.test(value) && url.$validator(value));

// Match the legacy body-only formats still supported by TemplateParameterConverterService.
export const normalizeTemplateParameters = (params = {}) => {
  const componentKeys = ['body', 'header', 'footer', 'buttons'];
  const isParameterMap = value =>
    value == null || (typeof value === 'object' && !Array.isArray(value));
  const validButtons =
    params.buttons == null ||
    (Array.isArray(params.buttons) &&
      params.buttons.every(
        button => button == null || (isParameterMap(button) && button.type)
      ));
  if (
    componentKeys.some(key => key in params) &&
    isParameterMap(params.body) &&
    isParameterMap(params.header) &&
    validButtons
  ) {
    return params;
  }
  const entries = Array.isArray(params)
    ? params.map((value, index) => [String(index + 1), String(value ?? '')])
    : Object.entries(params).map(([key, value]) => [key, String(value ?? '')]);
  return entries.length ? { body: Object.fromEntries(entries) } : {};
};
